import { useRef, useState } from "react";
import { Play } from "lucide-react";

/**
 * Affiliate VIDEO PLAYER — click to play. Does NOT autoplay.
 * Shows poster/thumbnail until the user hits play.
 * After first play: native HTML5 controls (play/pause, progress, mute, fullscreen).
 * Background hero video is separate and may stay muted autoplay.
 */
export const ExplainerVideo = ({ src, poster }) => {
  const ref = useRef(null);
  const [started, setStarted] = useState(false);
  const [failed, setFailed] = useState(false);

  const startPlayback = async () => {
    const el = ref.current;
    if (!el || started) return;
    try {
      // User gesture — unmute is allowed; keep sound available
      el.muted = false;
      await el.play();
      setStarted(true);
    } catch {
      setFailed(true);
    }
  };

  return (
    <div
      data-testid="explainer-video-frame"
      className="group relative w-full max-w-full overflow-hidden border border-[#00E5C0]/35 bg-black shadow-[0_0_40px_-12px_rgba(0,229,192,0.35)]"
    >
      {/* Explicit thumbnail until first play — browsers often skip poster if src preloads */}
      {!started && poster && (
        <img
          data-testid="explainer-poster"
          src={poster}
          alt=""
          className="pointer-events-none absolute inset-0 z-[1] aspect-video h-full w-full object-cover"
        />
      )}
      <video
        ref={ref}
        data-testid="explainer-video"
        src={src}
        poster={poster}
        playsInline
        preload="none"
        controls={started}
        onPlay={() => setStarted(true)}
        onError={() => setFailed(true)}
        className="block aspect-video h-auto w-full max-w-full bg-black object-cover"
      />
      {failed && (
        <p
          data-testid="explainer-video-error"
          className="border-t border-[#ff3b30]/30 bg-[#ff3b30]/5 px-4 py-3 text-center text-[10px] uppercase tracking-[0.2em] text-[#ff3b30]"
        >
          This video cannot play in your browser
        </p>
      )}
      {/* Big play + caption only before first play; after that native controls handle UX */}
      {!failed && !started && (
        <button
          type="button"
          data-testid="explainer-play-btn"
          onClick={startPlayback}
          aria-label="Play video"
          className="absolute inset-x-0 top-0 z-[2] flex aspect-video items-center justify-center bg-[#050606]/45 transition-colors hover:bg-[#050606]/25"
        >
          <span className="flex h-14 w-14 items-center justify-center rounded-full border border-[#00E5C0] bg-[#050606]/80 text-[#00E5C0] transition-transform group-hover:scale-110 sm:h-16 sm:w-16">
            <Play className="h-6 w-6 translate-x-0.5" />
          </span>
        </button>
      )}
      {!started && !failed && (
        <span className="pointer-events-none absolute bottom-3 left-3 right-3 z-[3] max-w-[95%] text-[9px] uppercase leading-snug tracking-[0.12em] text-[#00E5C0]/90 sm:tracking-[0.18em]">
          Tap play — Learn How You Can Make Real Money With PokeCoins.cc!
        </span>
      )}
    </div>
  );
};
